/**
 * Responsibility: turn a seed into sampleable continuous noise fields.
 *
 * Everything about the shape of the world traces back to a function created here.
 * Biome placement, terrain elevation, and structure jitter all sample fields built by
 * this module.
 *
 * Contains no Three.js and no DOM access, so it is fully unit testable.
 *
 * ## What the noise actually is
 *
 * Simplex noise maps a coordinate to a smooth value in roughly [-1, 1]. Nearby
 * coordinates get similar values, which is what makes it look like terrain rather than
 * static. Sampling it at several frequencies and summing the results with decreasing
 * amplitude gives fractal Brownian motion (fBm): low frequencies produce broad
 * landforms, high frequencies add fine detail.
 */

import { createNoise2D } from 'simplex-noise';
import { createRng, deriveSeed } from './rng.js';
import { NOISE } from '../config.js';

/**
 * Offset applied to each successive octave's sample coordinates.
 *
 * Without it, every octave samples the same field at multiplied frequencies, and all
 * octaves pass through zero at the origin together. That produces a visible flat spot
 * and faint cross-shaped artefacts along the world axes. Shifting each octave decorrelates
 * them. The value is arbitrary, just large and not a round number.
 */
const OCTAVE_OFFSET = 137.21;

/**
 * Build a plain simplex field from a seed and a purpose label.
 *
 * Prefer `createFbm` for terrain. Use this directly only when a single smooth octave is
 * genuinely what is wanted, such as a very broad biome suitability field.
 *
 * @param {number} seed - Base world seed.
 * @param {string} label - Purpose label, e.g. 'biome:mountains'. Keeps fields independent.
 * @returns {(x: number, y: number) => number} Samples in roughly [-1, 1].
 */
export function createNoiseField(seed, label) {
  return createNoise2D(createRng(deriveSeed(seed, label)));
}

/**
 * Build a layered fBm field.
 *
 * @param {number} seed - Base world seed.
 * @param {string} label - Purpose label. Keeps this field independent of all others.
 * @param {object} [options] - Overrides for the defaults in `config.js`. Supplied by the
 *   noise debug page so parameters can be tuned live.
 * @param {number} [options.octaves]
 * @param {number} [options.baseFrequency]
 * @param {number} [options.persistence]
 * @param {number} [options.lacunarity]
 * @returns {(x: number, y: number) => number} Samples in [-1, 1].
 */
export function createFbm(seed, label, options = {}) {
  const octaves = options.octaves ?? NOISE.OCTAVES;
  const baseFrequency = options.baseFrequency ?? NOISE.BASE_FREQUENCY;
  const persistence = options.persistence ?? NOISE.PERSISTENCE;
  const lacunarity = options.lacunarity ?? NOISE.LACUNARITY;

  if (!Number.isInteger(octaves) || octaves < 1) {
    throw new Error('createFbm: octaves must be an integer of at least 1');
  }
  if (!(baseFrequency > 0)) {
    throw new Error('createFbm: baseFrequency must be positive');
  }

  const noise2D = createNoiseField(seed, label);

  /*
    Sum of all octave amplitudes. Dividing by this guarantees the result stays within
    [-1, 1] no matter how many octaves or what persistence is used, so downstream code
    can rely on a fixed range and express terrain height as a plain multiplier.
  */
  let amplitudeSum = 0;
  let amplitude = 1;
  for (let i = 0; i < octaves; i += 1) {
    amplitudeSum += amplitude;
    amplitude *= persistence;
  }

  return function sampleFbm(x, y) {
    let total = 0;
    let frequency = baseFrequency;
    let octaveAmplitude = 1;

    for (let i = 0; i < octaves; i += 1) {
      const offset = i * OCTAVE_OFFSET;
      total += noise2D(x * frequency + offset, y * frequency + offset) * octaveAmplitude;
      frequency *= lacunarity;
      octaveAmplitude *= persistence;
    }

    return total / amplitudeSum;
  };
}

/**
 * Build a ridged fBm field, which produces sharp crests instead of rolling hills.
 *
 * Mountains want ridges, not blobs. Taking `1 - |noise|` folds the field at zero, so
 * what were zero crossings become sharp peaks. Squaring the result sharpens the crests
 * further and flattens the valleys, which reads much more like a mountain range.
 *
 * Output is in [0, 1] rather than [-1, 1], because a ridge has a floor but no natural
 * negative counterpart.
 *
 * @param {number} seed - Base world seed.
 * @param {string} label - Purpose label. Keeps this field independent of all others.
 * @param {object} [options] - Same overrides as `createFbm`.
 * @returns {(x: number, y: number) => number} Samples in [0, 1].
 */
export function createRidgedFbm(seed, label, options = {}) {
  const octaves = options.octaves ?? NOISE.OCTAVES;
  const baseFrequency = options.baseFrequency ?? NOISE.BASE_FREQUENCY;
  const persistence = options.persistence ?? NOISE.PERSISTENCE;
  const lacunarity = options.lacunarity ?? NOISE.LACUNARITY;

  if (!Number.isInteger(octaves) || octaves < 1) {
    throw new Error('createRidgedFbm: octaves must be an integer of at least 1');
  }
  if (!(baseFrequency > 0)) {
    throw new Error('createRidgedFbm: baseFrequency must be positive');
  }

  const noise2D = createNoiseField(seed, label);

  let amplitudeSum = 0;
  let amplitude = 1;
  for (let i = 0; i < octaves; i += 1) {
    amplitudeSum += amplitude;
    amplitude *= persistence;
  }

  return function sampleRidged(x, y) {
    let total = 0;
    let frequency = baseFrequency;
    let octaveAmplitude = 1;

    for (let i = 0; i < octaves; i += 1) {
      const offset = i * OCTAVE_OFFSET;
      const raw = noise2D(x * frequency + offset, y * frequency + offset);

      // Fold at zero to make crests, then square to sharpen them.
      const ridge = 1 - Math.abs(raw);
      total += ridge * ridge * octaveAmplitude;

      frequency *= lacunarity;
      octaveAmplitude *= persistence;
    }

    return total / amplitudeSum;
  };
}

/**
 * Remap a value from [-1, 1] into [0, 1].
 *
 * Useful for display and for treating a signed field as a probability or weight. Kept
 * here so the conversion has one authoritative home rather than being inlined wherever
 * a field needs displaying.
 *
 * @param {number} value
 * @returns {number}
 */
export function toUnitRange(value) {
  return (value + 1) / 2;
}
